'use client';

import { useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { getFriendlyErrorMessage } from '@/utils/errorMessages';

export default function Error({
    error,
    reset,
}: {
    error: Error & { digest?: string };
    reset: () => void;
}) {
    useEffect(() => {
        // Log the error to an error reporting service
        console.error('Application Error:', error);
    }, [error]);

    const { title, message, icon, variant } = getFriendlyErrorMessage(error);

    // Determine colors based on variant
    const getVariantClasses = (variant: string = 'danger') => {
        switch (variant) {
            case 'warning':
                return { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: 'text-yellow-600' };
            case 'info':
                return { bg: 'bg-blue-100', text: 'text-blue-700', icon: 'text-blue-600' };
            case 'danger':
            default:
                return { bg: 'bg-red-100', text: 'text-red-700', icon: 'text-red-600' };
        }
    };

    const variantClasses = getVariantClasses(variant);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
            <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center border border-gray-100">
                <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6 ${variantClasses.bg}`}>
                    <Icon name={icon as any} size={32} variant="outline" className={variantClasses.icon} />
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>

                <p className="text-gray-600 mb-6">
                    {message}
                </p>

                {/* In development, show error details */}
                {process.env.NODE_ENV === 'development' && (
                    <div className="mb-6 p-4 bg-red-50 rounded-lg text-left overflow-auto max-h-40">
                        <p className="text-xs font-mono text-red-800 break-words">{error.message}</p>
                        {error.digest && (
                            <p className="text-xs font-mono text-red-600 mt-1">Digest: {error.digest}</p>
                        )}
                    </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                        onClick={() => reset()}
                        className="px-6 py-2.5 bg-primary text-white font-semibold rounded-lg hover:bg-secondary transition-colors"
                    >
                        Try Again
                    </button>

                    <button
                        onClick={() => window.location.href = '/'}
                        className="px-6 py-2.5 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Go Home
                    </button>
                </div>
            </div>
        </div>
    );
}
